import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetCardsAddedQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const CardsAddedChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetCardsAddedQuery({ period });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Cards Added</h2>
          <p className="chart-subtitle">The number of cards added to the database</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend">
            <span className="legend-dot cards" />
            <span className="legend-label">Cards</span>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(label) => formatDate(label)}
                formatter={(value) => [`${Number(value).toLocaleString()} cards`, 'Added']}
              />
              <Bar
                dataKey="count"
                fill="#DD5BEA"
                radius={[4, 4, 0, 0]}
                name="Cards Added"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total: {data.totalCardsAdded.toLocaleString()} cards added in the last {period === 7 ? 'week' : 'month'}
        </div>
      )}
    </div>
  );
};

export default CardsAddedChart;
